<script setup lang="ts">
import { ref } from 'vue'
import StemneButton from '@/components/ui/StemneButton.vue'
import StemneModal from '@/components/ui/StemneModal.vue'
import { usePasskeySudoReauth } from '@/composables/usePasskeySudoReauth'

// Re-auth modal for the passkey "sudo" window (#1042, #1190).
//
// Triggered by apiClient.ts when a passkey add/rename/remove call returns
// 403 passkey_sudo_required. The shell wires this component to the global
// passkey-sudo prompter via setPasskeySudoPrompter; this component owns
// the visible state and the WebAuthn ceremony, the prompter just exposes
// "open the modal, await the user's choice".
//
// Two terminal states from the caller's perspective:
//   - confirm: the operator authenticated → emit('done', true) → caller
//     retries the original request
//   - cancel: the operator dismissed (or repeatedly failed) → emit('done',
//     false) → caller surfaces the original 403

defineProps<{
  show: boolean
}>()

const emit = defineEmits<{
  done: [ok: boolean]
}>()

const { reauth } = usePasskeySudoReauth()

const busy = ref(false)
const errorMsg = ref('')

async function onConfirm() {
  busy.value = true
  errorMsg.value = ''
  const res = await reauth()
  busy.value = false
  if (res.ok) {
    emit('done', true)
    return
  }
  // Leave the modal open so the operator can retry. The Cancel button
  // (or the close X / ESC) exits with done(false).
  errorMsg.value = res.error
}

function onCancel() {
  if (busy.value) return
  errorMsg.value = ''
  emit('done', false)
}
</script>

<template>
  <StemneModal
    :show="show"
    :title="$t('uiStemnePasskeySudoModal.title')"
    width="sm"
    tone="neutral"
    :show-close-button="!busy"
    :locked="busy"
    :dismiss-on-overlay="false"
    @close="onCancel"
  >
    <p class="sudo-body">
      {{ $t('uiStemnePasskeySudoModal.body') }}
    </p>
    <p v-if="errorMsg" class="sudo-error" role="alert">{{ errorMsg }}</p>

    <template #footer>
      <StemneButton variant="quiet" type="button" :disabled="busy" @click="onCancel">
        {{ $t('common.abort') }}
      </StemneButton>
      <StemneButton
        variant="primary"
        type="button"
        :loading="busy"
        :disabled="busy"
        @click="onConfirm"
      >
        {{ $t('uiStemnePasskeySudoModal.title') }}
      </StemneButton>
    </template>
  </StemneModal>
</template>

<style lang="scss" scoped>
.sudo-body {
  font-family: var(--font-sans);
  font-size: var(--fs-body);
  color: var(--fg-muted);
  margin: 0;
}
.sudo-error {
  font-family: var(--font-sans);
  font-size: var(--fs-body);
  color: var(--color-rust, #8a3a24);
  margin: 12px 0 0;
}
</style>
